import {
  AlertCircle,
  Bell,
  Bike,
  CalendarDays,
  Download,
  HelpCircle,
  MapPinned,
  Plus,
  Route,
  ShieldCheck,
  Smartphone,
  UserCircle,
  Users,
  type LucideIcon
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/useAuth";
import { ActionButton } from "../components/buttons/ActionButton";
import { EmptyState } from "../components/empty/EmptyState";
import {
  eventStatusLabel,
  eventTypeLabel,
  formatEventDistance,
  getEffectiveEventStatus,
  getManagementUserProfile,
  getMyLiveActionAccess,
  LiveActionAccess,
  PulsEvent,
  subscribeUserEvents
} from "../data/eventService";
import { loadUserRides, UserRideSummary } from "../data/rideService";
import { PendingActionsSummary, subscribeMyPendingActionsSummary } from "../data/actionsService";
import { useI18n } from "../i18n";
import { formatDateTime, formatDistanceKm } from "../lib/format";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=gr.puls.app";

const emptyPendingSummary: PendingActionsSummary = {
  total: 0,
  invitations: 0,
  supportControl: 0,
  help: 0,
  loading: false,
  error: null
};

type DashboardMetricProps = {
  label: string;
  value: number | string;
  hint?: string;
};

function DashboardMetric({ hint, label, value }: DashboardMetricProps) {
  return (
    <div className="dashboard-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

function SectionCard({
  actions,
  children,
  description,
  error,
  icon: Icon,
  loading,
  title
}: {
  actions?: React.ReactNode;
  children: React.ReactNode;
  description: string;
  error?: string | null;
  icon: LucideIcon;
  loading?: boolean;
  title: string;
}) {
  const { t } = useI18n();

  return (
    <article className="card dashboard-overview-card">
      <div className="dashboard-card-header">
        <div className="dashboard-card-title">
          <span className="dashboard-card-icon" aria-hidden="true">
            <Icon size={19} />
          </span>
          <div>
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
        </div>
        {actions ? <div className="quick-actions">{actions}</div> : null}
      </div>

      {loading ? <p className="dashboard-section-note">{t("common.loading")}</p> : null}
      {!loading && error ? (
        <p className="dashboard-section-note error">
          <AlertCircle size={16} /> {t("dashboard.sectionLoadError")}
        </p>
      ) : null}
      {!loading && !error ? children : null}
    </article>
  );
}

function CompactEventList({ empty, events }: { empty: string; events: PulsEvent[] }) {
  if (events.length === 0) return <p className="dashboard-section-note">{empty}</p>;

  return (
    <div className="dashboard-compact-list">
      {events.map((event) => {
        const status = getEffectiveEventStatus(event);
        return (
          <ActionButton key={event.id} to={`/event/${event.id}`} variant="ghost">
            <span>
              <strong>{event.title}</strong>
              <small>
                {eventTypeLabel(event.eventType)} · {eventStatusLabel(status)} · {formatDateTime(event.startDate)}
              </small>
            </span>
          </ActionButton>
        );
      })}
    </div>
  );
}

function CompactRouteList({ routes }: { routes: UserRideSummary[] }) {
  const { t } = useI18n();

  if (routes.length === 0) {
    return <p className="dashboard-section-note">{t("dashboard.noRoutes")}</p>;
  }

  return (
    <div className="dashboard-compact-list">
      {routes.map((route) => (
        <ActionButton key={route.id} to={`/routes/${route.id}`} variant="ghost">
          <span>
            <strong>{route.title}</strong>
            <small>
              {formatDistanceKm(route.distanceMeters)} · {formatDateTime(route.date)}
            </small>
          </span>
        </ActionButton>
      ))}
    </div>
  );
}

function liveAccessRoleLabel(role: LiveActionAccess["primaryRole"], t: (key: string) => string) {
  switch (role) {
    case "ORGANIZER":
      return t("role.organizer");
    case "SUPPORT":
      return t("role.support");
    case "CONTROL":
      return t("role.control");
    case "ADMIN":
      return t("role.admin");
    default:
      return role;
  }
}

export function DashboardPage() {
  const { loading: authLoading, signIn, user } = useAuth();
  const { t } = useI18n();
  const [pendingSummary, setPendingSummary] = useState<PendingActionsSummary>(emptyPendingSummary);
  const [events, setEvents] = useState<PulsEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [routes, setRoutes] = useState<UserRideSummary[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routesError, setRoutesError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setPendingSummary(emptyPendingSummary);
      return;
    }

    return subscribeMyPendingActionsSummary(user, setPendingSummary);
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    async function loadProfileAccess() {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const profile = await getManagementUserProfile(user);
        if (!cancelled) setIsAdmin(profile.isAdmin);
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    }

    loadProfileAccess();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setEvents([]);
      setEventsLoading(false);
      setEventsError(null);
      return;
    }

    return subscribeUserEvents(
      user.uid,
      (state) => {
        setEvents(state.events);
        setEventsLoading(state.loading);
        setEventsError(state.error);
      },
      { includeAdminEvents: isAdmin }
    );
  }, [isAdmin, user]);

  useEffect(() => {
    let cancelled = false;

    async function loadRoutes() {
      if (!user) {
        setRoutes([]);
        setRoutesLoading(false);
        setRoutesError(null);
        return;
      }

      setRoutesLoading(true);
      setRoutesError(null);
      try {
        const nextRoutes = await loadUserRides(user.uid);
        if (!cancelled) setRoutes(nextRoutes);
      } catch (error) {
        if (!cancelled) setRoutesError(error instanceof Error ? error.message : "routes-load-error");
      } finally {
        if (!cancelled) setRoutesLoading(false);
      }
    }

    loadRoutes();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const organizerEvents = useMemo(
    () => (user ? events.filter((event) => event.organizerId === user.uid) : []),
    [events, user]
  );
  const activeOrganizerEvents = useMemo(
    () =>
      organizerEvents
        .filter((event) => {
          const status = getEffectiveEventStatus(event);
          return status === "UPCOMING" || status === "ONGOING";
        })
        .slice(0, 4),
    [organizerEvents]
  );
  const liveEvents = useMemo(
    () =>
      user
        ? events
            .map((event) => {
              const access = getMyLiveActionAccess(event, user.uid, isAdmin);
              return access ? { access, event } : null;
            })
            .filter((item): item is { access: LiveActionAccess; event: PulsEvent } => item !== null)
            .sort((a, b) => (a.event.startDate ?? 0) - (b.event.startDate ?? 0))
            .slice(0, 3)
        : [],
    [events, isAdmin, user]
  );
  const recentRoutes = useMemo(() => routes.slice(0, 4), [routes]);

  if (authLoading) {
    return (
      <section className="page">
        <EmptyState icon={Bell} title={t("dashboard.loadingTitle")} description={t("dashboard.loadingBody")} />
      </section>
    );
  }

  if (!user) {
    return (
      <section className="page">
        <div className="page-intro">
          <div>
            <h1>{t("dashboard.publicTitle")}</h1>
            <p>{t("dashboard.publicBody")}</p>
          </div>
        </div>

        <article className="card dashboard-welcome-card">
          <div>
            <span className="pill">
              <Smartphone size={14} /> PULS Web
            </span>
            <h2>{t("dashboard.welcomeTitle")}</h2>
            <p>{t("dashboard.welcomeBody")}</p>
          </div>
          <div className="quick-actions">
            <ActionButton onClick={signIn}>{t("common.signInGoogle")}</ActionButton>
            <ActionButton to="/events" variant="ghost">
              {t("common.viewActions")}
            </ActionButton>
            <a className="action-button ghost" href={PLAY_STORE_URL} rel="noreferrer" target="_blank">
              <Download size={16} /> {t("common.downloadPuls")}
            </a>
          </div>
        </article>
      </section>
    );
  }

  return (
    <section className="page">
      <div className="page-intro">
        <div>
          <h1>{t("dashboard.title")}</h1>
          <p>{t("dashboard.subtitle")}</p>
        </div>
        <div className="quick-actions">
          <ActionButton to="/actions/new">
            <Plus size={17} /> {t("dashboard.newAction")}
          </ActionButton>
          <ActionButton to="/route-editor" variant="ghost">
            <MapPinned size={17} /> {t("dashboard.newRoute")}
          </ActionButton>
        </div>
      </div>

      <div className="dashboard-overview-grid">
        <SectionCard
          actions={<ActionButton to="/actions">{t("dashboard.openTasks")}</ActionButton>}
          description={t("dashboard.pendingDescription")}
          error={pendingSummary.error}
          icon={Bell}
          loading={pendingSummary.loading}
          title={t("dashboard.pendingTitle")}
        >
          <div className="dashboard-metric-grid">
            <DashboardMetric label={t("dashboard.total")} value={pendingSummary.total} />
            <DashboardMetric label={t("nav.invitations")} value={pendingSummary.invitations} />
            <DashboardMetric label={t("nav.supportControl")} value={pendingSummary.supportControl} />
            <DashboardMetric label={t("nav.sosHelp")} value={pendingSummary.help} />
          </div>
        </SectionCard>

        <SectionCard
          actions={
            <>
              <ActionButton to="/actions/new">{t("dashboard.newAction")}</ActionButton>
              <ActionButton to="/my-events" variant="ghost">
                {t("dashboard.myActionsTitle")}
              </ActionButton>
            </>
          }
          description={t("dashboard.myActionsDescription")}
          error={eventsError}
          icon={Bike}
          loading={eventsLoading}
          title={t("dashboard.myActionsTitle")}
        >
          <CompactEventList empty={t("dashboard.myActionsEmpty")} events={activeOrganizerEvents} />
        </SectionCard>

        <SectionCard
          actions={
            <>
              <ActionButton to="/routes">{t("dashboard.myRoutesTitle")}</ActionButton>
              <ActionButton to="/route-editor" variant="ghost">
                Route Editor
              </ActionButton>
            </>
          }
          description={t("dashboard.myRoutesDescription")}
          error={routesError}
          icon={Route}
          loading={routesLoading}
          title={t("dashboard.myRoutesTitle")}
        >
          <div className="dashboard-section-stack">
            <div className="dashboard-metric-grid compact">
              <DashboardMetric label={t("dashboard.total")} value={routes.length} hint={t("dashboard.synced")} />
              <DashboardMetric
                label={t("dashboard.recent")}
                value={recentRoutes[0] ? formatDistanceKm(recentRoutes[0].distanceMeters) : "-"}
                hint={recentRoutes[0]?.title}
              />
            </div>
            <CompactRouteList routes={recentRoutes} />
          </div>
        </SectionCard>

        <SectionCard
          description={t("dashboard.liveNowDescription")}
          error={eventsError}
          icon={Users}
          loading={eventsLoading}
          title={t("dashboard.liveNowTitle")}
        >
          {liveEvents.length === 0 ? (
            <p className="dashboard-section-note">{t("dashboard.liveAccessEmpty")}</p>
          ) : (
            <div className="dashboard-compact-list">
              {liveEvents.map(({ access, event }) => (
                <ActionButton key={event.id} to={`/race/${event.id}`} variant="ghost">
                  <span>
                    <strong>{event.title}</strong>
                    <small>
                      {eventTypeLabel(event.eventType)} · {eventStatusLabel(getEffectiveEventStatus(event))} ·{" "}
                      {formatDateTime(event.startDate)}
                    </small>
                    <small>
                      <span className="dashboard-live-role">{liveAccessRoleLabel(access.primaryRole, t)}</span> ·{" "}
                      {formatEventDistance(event)} · {t("dashboard.openLive")}
                    </small>
                  </span>
                </ActionButton>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard
          description={t("dashboard.shortcutsDescription")}
          icon={ShieldCheck}
          title={t("dashboard.shortcutsTitle")}
        >
          <div className="dashboard-shortcut-grid">
            <ActionButton to="/actions/new">
              <Plus size={16} /> {t("dashboard.newAction")}
            </ActionButton>
            <ActionButton to="/route-editor" variant="ghost">
              <MapPinned size={16} /> {t("dashboard.newRoute")}
            </ActionButton>
            <ActionButton to="/route-editor" variant="ghost">
              <Download size={16} /> {t("dashboard.importRoute")}
            </ActionButton>
            <ActionButton to="/actions" variant="ghost">
              <HelpCircle size={16} /> {t("nav.tasks")}
            </ActionButton>
            <ActionButton to="/profile" variant="ghost">
              <UserCircle size={16} /> {t("nav.profile")}
            </ActionButton>
          </div>
        </SectionCard>
      </div>
    </section>
  );
}
